import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Sparkles } from "lucide-react";
import { MoodCard } from "../components/MoodCard";
import { getPublicExplore } from "../lib/api";
import { listSportsGames } from "../lib/sportsApi";
import { track } from "../lib/track";
import MarqueeRail from "../components/MarqueeRail";

/**
 * /discover (and legacy /explore) — the public gallery.
 *
 * A scrollable wall of recent shareable cards from named senders. No
 * faves, no scores, no infinite-scroll bait — just finished thoughts in
 * a grid. Tapping any card opens it via /open/<slug>. The CTA at the
 * bottom funnels visitors back into make-your-own.
 *
 * iter-84: renamed to "Discover" + added category tabs that filter by
 * mood-lane. "Trending" is the default (no lane filter; surfaces editor
 * picks + recents). Each tab maps a friendly label (Love, Friendship…)
 * to an existing backend lane key.
 */
const PAGE_SIZE = 24;

// Public-facing tabs → backend lane keys. Keep label SHORT (one word) so
// the strip scrolls comfortably on mobile. Tabs without a lane filter
// just show the unfiltered "trending" stream.
const TABS = [
    { key: "trending",     label: "trending",     lane: null,      emoji: "🔥" },
    { key: "love",         label: "love",         lane: "heart",   emoji: "❤️" },
    { key: "friendship",   label: "friendship",   lane: "banter",  emoji: "🤝" },
    { key: "funny",        label: "funny",        lane: "funny",   emoji: "😂" },
    { key: "deep",         label: "deep",         lane: "life",    emoji: "💭" },
    { key: "faith",        label: "faith",        lane: "faith",   emoji: "🙏" },
    { key: "drive",        label: "drive",        lane: "drive",   emoji: "▲" },
    { key: "celebrations", label: "celebrations", lane: "life",    emoji: "🎉" },
    { key: "healing",      label: "healing",      lane: "healing", emoji: "✺" },
];

export default function Explore() {
    const navigate = useNavigate();
    const location = useLocation();
    const [items, setItems] = useState(null);
    const [error, setError] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    // Tab state — defaults to "trending"; resets the list when changed.
    const [activeTab, setActiveTab] = useState("trending");
    const [sportsGames, setSportsGames] = useState([]);
    const isDiscoverRoute = location.pathname.startsWith("/discover");
    const pageTitle = isDiscoverRoute ? "Discover" : "Explore";
    const eyebrow = isDiscoverRoute ? "discover" : "the gallery";

    const activeLane = TABS.find((t) => t.key === activeTab)?.lane || null;

    useEffect(() => {
        let cancelled = false;
        setItems(null);
        setError(false);
        setOffset(0);
        setHasMore(true);
        getPublicExplore(PAGE_SIZE, 0, activeLane)
            .then((data) => {
                if (cancelled) return;
                setItems(data.items || []);
                setHasMore((data.items || []).length === PAGE_SIZE);
                track("explore_viewed", { count: data.count, tab: activeTab });
            })
            .catch(() => { if (!cancelled) setError(true); });
        return () => { cancelled = true; };
    }, [activeTab, activeLane]);

    // iter-85 — fetch active sports games once for the "Sports Moments"
    // tile shown above the category tabs. Best-effort, silent on failure.
    useEffect(() => {
        if (!isDiscoverRoute) return;
        let cancelled = false;
        listSportsGames()
            .then((games) => { if (!cancelled) setSportsGames(games || []); })
            .catch(() => { /* silent */ });
        return () => { cancelled = true; };
    }, [isDiscoverRoute]);


    const loadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const nextOffset = offset + PAGE_SIZE;
            const data = await getPublicExplore(PAGE_SIZE, nextOffset, activeLane);
            const more = data.items || [];
            setItems((prev) => [...(prev || []), ...more]);
            setOffset(nextOffset);
            setHasMore(more.length === PAGE_SIZE);
        } catch { /* silent */ }
        finally { setLoadingMore(false); }
    };

    return (
        <div className="relative min-h-screen w-full aurora-wash" data-testid="explore-screen">
            <Helmet>
                <title>{`${pageTitle} — MoodCard`}</title>
                <meta name="description" content="A quiet gallery of finished thoughts — anonymous MoodCards to browse. Get inspired. Make your own." />
                <meta property="og:title" content={`${pageTitle} — MoodCard`} />
                <meta property="og:description" content="A gallery of finished thoughts. Three honest ways to say what you're feeling." />
            </Helmet>

            <div className="absolute inset-0 grain pointer-events-none" />

            {/* Top bar */}
            <header className="sticky top-0 z-10 max-w-md lg:max-w-5xl mx-auto px-5 pt-5 pb-3 backdrop-blur-md"
                style={{ backgroundColor: "color-mix(in srgb, var(--surface-1) 78%, transparent)" }}
            >
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => navigate("/")}
                        data-testid="explore-back"
                        className="btn-press inline-flex items-center gap-2 font-sans text-[10px] uppercase tracking-[0.32em] text-app-3 hover:text-app transition-colors"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                        back
                    </button>
                    <div className="flex items-center gap-2" data-testid="explore-brand-pill">
                        <span
                            className="flex items-center justify-center h-5 w-5 rounded-[4px]"
                            style={{ border: "1.5px solid #34D399", boxShadow: "0 0 8px rgba(52, 211, 153, 0.18)" }}
                        >
                            <span className="block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#34D399" }} />
                        </span>
                        <span className="font-sans text-xs uppercase tracking-[0.35em] text-app">
                            MOODCARD
                        </span>
                    </div>
                </div>
            </header>

            <main className="relative z-10 max-w-md lg:max-w-5xl mx-auto px-6 pt-8 pb-24">
                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3 mb-3">
                        {eyebrow}
                    </p>
                    <h1 className="font-serif font-light text-app text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight" data-testid="explore-headline">
                        finished thoughts<br />
                        <span className="italic text-app-2">from people, lately.</span>
                    </h1>
                </motion.div>

                {/* iter-85 — Sports Moments tile. Shown only on /discover
                    above the category tabs when there's at least one active
                    sports game in the registry. Funnels viewers into
                    /r/<game-slug> for the side-picker + curated deck. */}
                {isDiscoverRoute && sportsGames.length > 0 && (
                    <div className="mt-7 space-y-3" data-testid="discover-sports-moments">
                        <div className="text-[10px] tracking-[0.32em] uppercase text-app-3">
                            🔥 sports moments
                        </div>
                        {sportsGames.slice(0, 3).map((g) => {
                            const isLive = g.status === "live";
                            return (
                                <button
                                    key={g.game_slug}
                                    type="button"
                                    onClick={() => navigate(`/r/${g.game_slug}`)}
                                    data-testid={`discover-sports-tile-${g.game_slug}`}
                                    className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl border border-white/12 bg-black/30 hover:border-amber-300/40 transition-colors text-left"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase mb-1">
                                            {isLive ? (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-500/20 border border-rose-400/40 text-rose-200">
                                                    <span className="w-1 h-1 rounded-full bg-rose-400 animate-pulse" />
                                                    live now
                                                </span>
                                            ) : (
                                                <span className="text-app-3">tonight</span>
                                            )}
                                            <span className="text-app-3">·</span>
                                            <span className="text-app-2">{g.broadcast || "league pass"}</span>
                                        </div>
                                        <div className="font-display text-base text-app uppercase tracking-wide truncate">
                                            {g.label || `${g.away_team?.short} vs ${g.home_team?.short}`}
                                        </div>
                                        <div className="mt-0.5 font-serif italic text-[12px] text-app-2 truncate">
                                            fresh reactions ready · pick your side
                                        </div>
                                    </div>
                                    <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400 text-black text-[11px] tracking-[0.18em] uppercase font-medium">
                                        open
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* iter-84 — category tabs. Horizontal scroll on mobile,
                    centered on desktop. "trending" is the default (no
                    lane filter). Switching tabs re-fetches with ?lane=. */}
                <MarqueeRail
                    className="mt-8 -mx-6 px-6 pb-1"
                    testId="discover-tabs"
                    speed={0.18}
                >
                    {TABS.map((t) => {
                        const active = activeTab === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setActiveTab(t.key)}
                                data-testid={`discover-tab-${t.key}`}
                                aria-pressed={active}
                                className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-sans text-[11px] tracking-[0.18em] uppercase transition-all ${
                                    active
                                        ? "bg-amber-400 text-black border border-amber-300"
                                        : "bg-black/25 text-app-2 border border-white/12 hover:border-amber-300/40 hover:text-app"
                                }`}
                            >
                                <span className="text-[13px]" aria-hidden="true">{t.emoji}</span>
                                {t.label}
                            </button>
                        );
                    })}
                </MarqueeRail>

                {/* Grid */}
                {!items && !error && (
                    <div className="mt-12 flex items-center justify-center" data-testid="explore-loading">
                        <span className="h-1.5 w-1.5 rounded-full pulse-soft" style={{ background: "#d4af6e" }} />
                    </div>
                )}

                {error && (
                    <p className="mt-12 text-center font-serif italic text-app-3 text-lg" data-testid="explore-error">
                        the gallery is quiet right now — try again in a moment.
                    </p>
                )}

                {items && items.length === 0 && (
                    <p className="mt-12 text-center font-serif italic text-app-2 text-lg" data-testid="explore-empty">
                        {activeTab === "trending"
                            ? "no cards yet. be the first."
                            : `nothing in ${activeTab} yet — be the first.`}
                    </p>
                )}

                {items && items.length > 0 && (
                    <>
                        <div
                            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
                            data-testid="explore-grid"
                        >
                            {items.map((it, i) => (
                                <motion.button
                                    key={it.id}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.6), ease: [0.16, 1, 0.3, 1] }}
                                    whileHover={{ y: -4 }}
                                    onClick={() => navigate(`/open/${it.slug}`)}
                                    data-testid={`explore-card-${it.id}`}
                                    className="btn-press text-left group"
                                >
                                    {it.card ? (
                                        <MoodCard
                                            card={it.card}
                                            moodId={it.id}
                                            showWatermark={true}
                                            lane={null}
                                            temperature={null}
                                        />
                                    ) : (
                                        <div className="aspect-[9/16] rounded-2xl bg-surface-1 flex items-center justify-center">
                                            <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3">missing</span>
                                        </div>
                                    )}
                                    <div className="mt-3 flex items-center justify-between px-1">
                                        {/* iter-105 — author names removed for privacy; show mood/style label instead. */}
                                        <span className="font-sans text-[10px] uppercase tracking-[0.32em] text-app-2 group-hover:text-app transition-colors">
                                            {it.mood_label || "a quiet feeling"}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            {it.pinned && (
                                                <span
                                                    className="font-sans text-[9px] uppercase tracking-[0.32em] text-amber-200/85"
                                                    title="Editor's pick"
                                                    data-testid={`explore-card-pinned-${it.id}`}
                                                >
                                                    ✦ picked
                                                </span>
                                            )}
                                            <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-app-3">
                                                {it.style || "poetic"}
                                            </span>
                                        </div>
                                    </div>
                                </motion.button>
                            ))}
                        </div>

                        {hasMore && (
                            <div className="mt-12 flex justify-center">
                                <button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    data-testid="explore-load-more"
                                    className="btn-press font-sans text-[11px] uppercase tracking-[0.32em] text-app-2 hover:text-app border border-app/20 hover:border-app/40 px-5 py-2.5 rounded-full transition-colors disabled:opacity-40"
                                >
                                    {loadingMore ? "loading…" : "load more"}
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* CTA — funnel back to make-your-own */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="mt-20 pt-12 border-t border-app/15 text-center"
                >
                    <p className="font-serif italic text-app-2 text-lg mb-5">
                        make your own?
                    </p>
                    <button
                        onClick={() => { track("explore_cta_clicked"); navigate("/"); }}
                        data-testid="explore-make-own"
                        className="btn-press btn-metal inline-flex items-center gap-2 py-3 px-6 rounded-full font-sans text-xs font-medium tracking-wide"
                    >
                        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Make my MoodCard
                    </button>
                </motion.div>

                {/* Legal stamp */}
                <footer className="mt-16 text-center">
                    <p className="font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 opacity-65">
                        moodcard · a product of <span className="text-app-2">zephiron group llc</span> · wyoming, usa
                    </p>
                </footer>
            </main>
        </div>
    );
}
